<script setup>
/* global axios */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Icon from 'next/icon/Icon.vue';
import SearchAPI from 'dashboard/api/search';
import { useAlert } from 'dashboard/composables';
import {
  parseLegacySeamCallContent,
  parseSeamCallContent,
} from 'dashboard/components-next/message/bubbles/vcard.mjs';
import { seamChatForwardText } from 'dashboard/components-next/message/forwardSelection.mjs';

const props = defineProps({
  message: { type: Object, default: null },
  messages: { type: Array, default: () => [] },
  sourceInboxId: { type: [Number, String], default: null },
});

const emit = defineEmits(['close', 'forwarded']);
const { locale } = useI18n();
const query = ref('');
const candidates = ref([]);
const isSearching = ref(false);
const forwardingConversationId = ref(null);
let searchRevision = 0;

const text = (key, values) => seamChatForwardText(locale.value, key, values);

const messagesToForward = computed(() => {
  if (props.messages.length > 0) return props.messages;
  return props.message ? [props.message] : [];
});

const sourceConversationId = computed(
  () => messagesToForward.value[0]?.conversation_id
);

const messagePreview = computed(() => {
  if (messagesToForward.value.length > 1) {
    return text('dialog.messages', { count: messagesToForward.value.length });
  }

  const content = String(messagesToForward.value[0]?.content || '').trim();
  if (parseSeamCallContent(content) || parseLegacySeamCallContent(content)) {
    return text('dialog.call');
  }
  if (/BEGIN:VCARD/i.test(content)) return text('dialog.vcard');
  if (content) return content.replace(/\s+/g, ' ').slice(0, 180);
  return text('dialog.media');
});

function displayName(conversation) {
  const contact = conversation.contact || {};
  return (
    contact.name ||
    contact.phone_number ||
    contact.email ||
    text('dialog.conversation', { id: conversation.id })
  );
}

function initial(conversation) {
  return displayName(conversation).trim().slice(0, 1).toUpperCase() || 'W';
}

watch(query, async value => {
  const revision = ++searchRevision;
  const term = value.trim();
  if (term.length < 2) {
    candidates.value = [];
    isSearching.value = false;
    return;
  }

  isSearching.value = true;
  try {
    const { data } = await SearchAPI.conversations({ q: term });
    if (revision !== searchRevision) return;
    const rows = data?.payload?.conversations || [];
    candidates.value = rows
      .filter(conversation => {
        const sameInbox =
          Number(conversation.inbox?.id) === Number(props.sourceInboxId);
        return sameInbox && Number(conversation.id) !== Number(sourceConversationId.value);
      })
      .slice(0, 15);
  } catch {
    if (revision === searchRevision) {
      candidates.value = [];
      useAlert(text('dialog.error'));
    }
  } finally {
    if (revision === searchRevision) isSearching.value = false;
  }
});

async function forwardTo(conversation) {
  if (forwardingConversationId.value || messagesToForward.value.length === 0) return;
  forwardingConversationId.value = conversation.id;

  try {
    const accountId = window.location.pathname.split('/')[3];
    await axios.post(
      `/api/v1/accounts/${accountId}/conversations/${sourceConversationId.value}/messages/whatsapp_forward`,
      {
        message_ids: messagesToForward.value.map(message => message.id),
        target_conversation_id: conversation.id,
      }
    );
    const count = messagesToForward.value.length;
    useAlert(
      count === 1
        ? text('dialog.success.single')
        : text('dialog.success.multiple', { count })
    );
    emit('forwarded', count);
    emit('close');
  } catch (error) {
    const code = error?.response?.data?.error_code;
    useAlert(
      code
        ? text(`error.${code}`)
        : error?.response?.data?.error || text('dialog.error')
    );
  } finally {
    forwardingConversationId.value = null;
  }
}
</script>

<template>
  <section class="w-full max-w-xl p-6" aria-labelledby="seam-chat-forward-title">
    <div>
      <div>
        <p class="text-sm font-medium text-n-blue-11">WhatsApp</p>
        <h2 id="seam-chat-forward-title" class="mt-1 text-lg font-semibold text-n-slate-12">
          {{ messagesToForward.length > 1 ? text('dialog.title.multiple') : text('dialog.title.single') }}
        </h2>
      </div>
    </div>

    <p class="mt-2 text-sm text-n-slate-11">
      {{ text('dialog.description') }}
    </p>

    <div class="mt-4 rounded-lg border border-n-container bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-11">
      <span class="mr-2 font-medium text-n-slate-12">{{ text('dialog.forwarding') }}</span>
      {{ messagePreview }}
    </div>

    <label class="mt-5 flex h-11 items-center gap-2 rounded-lg border border-n-container bg-n-background px-3 focus-within:border-n-brand focus-within:ring-2 focus-within:ring-n-brand/20">
      <span class="sr-only">{{ text('dialog.search.aria') }}</span>
      <Icon
        icon="i-lucide-search"
        class="pointer-events-none size-4 shrink-0 text-n-slate-10"
      />
      <input
        v-model="query"
        type="text"
        inputmode="search"
        autocomplete="off"
        :placeholder="text('dialog.search.placeholder')"
        class="min-w-0 flex-1 appearance-none bg-transparent text-sm text-n-slate-12 outline-none placeholder:text-n-slate-10"
      />
    </label>

    <div class="mt-3 min-h-24 overflow-hidden rounded-lg border border-n-container">
      <div v-if="isSearching" class="p-4 text-sm text-n-slate-11">
        {{ text('dialog.searching') }}
      </div>
      <div v-else-if="query.trim().length < 2" class="p-4 text-sm text-n-slate-11">
        {{ text('dialog.search.hint') }}
      </div>
      <div v-else-if="candidates.length === 0" class="p-4 text-sm text-n-slate-11">
        {{ text('dialog.search.empty') }}
      </div>
      <ul v-else class="divide-y divide-n-container">
        <li v-for="conversation in candidates" :key="conversation.id">
          <button
            type="button"
            class="flex w-full items-center gap-3 px-3 py-3 text-left hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-n-brand disabled:cursor-wait disabled:opacity-60"
            :disabled="Boolean(forwardingConversationId)"
            @click="forwardTo(conversation)"
          >
            <span class="grid size-9 shrink-0 place-content-center rounded-full bg-n-solid-blue text-sm font-medium text-white">
              {{ initial(conversation) }}
            </span>
            <span class="min-w-0 flex-1">
              <span class="block truncate text-sm font-medium text-n-slate-12">
                {{ displayName(conversation) }}
              </span>
              <span class="block truncate text-xs text-n-slate-11">
                {{ conversation.contact?.phone_number || conversation.inbox?.name || 'WhatsApp' }}
              </span>
            </span>
            <Icon
              :icon="forwardingConversationId === conversation.id ? 'i-lucide-loader-circle' : 'i-lucide-forward'"
              class="size-4 shrink-0 text-n-slate-10"
              :class="{ 'animate-spin': forwardingConversationId === conversation.id }"
              aria-hidden="true"
            />
          </button>
        </li>
      </ul>
    </div>
  </section>
</template>
